import React, { useState } from 'react';
import { useListContext, useNotify, usePermissions, useRefresh } from 'react-admin';
import { gql } from 'apollo-boost';
import {
    Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField,
} from '@mui/material';
import { client } from '../helpers/apolloClient';

// Setting the standing allowance in bulk. Each selected beneficiary is written
// through the same finance-guarded mutation the single panel uses, so every one
// still gets its own history entry rather than a bulk row that skips the audit.
const SET_ALLOWANCE = gql`
    mutation setMonthlyAllowance($record: SetMonthlyAllowanceInput!) {
        setMonthlyAllowance(record: $record) { id monthlyAllowance }
    }
`;

const SetAllowanceButton = () => {
    const { selectedIds = [], onUnselectItems } = useListContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    const close = () => { if (!busy) setOpen(false); };

    const apply = async () => {
        let value = null;
        if (String(amount).trim() !== '') {
            value = Number(amount);
            if (!Number.isInteger(value) || value < 0) {
                notify('The monthly allowance must be a whole, non-negative number of birr.', { type: 'warning' });
                return;
            }
        }

        setBusy(true);
        try {
            const results = await Promise.allSettled(selectedIds.map(id =>
                client.mutate({
                    mutation: SET_ALLOWANCE,
                    variables: { record: { id, amount: value, note: note.trim() || undefined } },
                })
            ));
            const failed = results.filter(r => r.status === 'rejected').length;
            const done = results.length - failed;

            if (failed) {
                notify(`Set the allowance for ${done} of ${results.length}; ${failed} failed.`, { type: 'warning' });
            } else {
                notify(
                    value === null
                        ? `Cleared the allowance for ${done}`
                        : `Set ${value} ETB for ${done} ${done === 1 ? 'beneficiary' : 'beneficiaries'}`
                );
            }

            onUnselectItems();
            setNote('');
            setOpen(false);
            refresh();
        } finally {
            setBusy(false);
        }
    };

    const count = selectedIds.length;

    return (
        <>
            <Button size="small" color="primary" disabled={!count} onClick={() => setOpen(true)}>
                Set payment
            </Button>
            <Dialog open={open} onClose={close}>
                <DialogTitle>Set monthly allowance</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 1 }}>
                        Applies to {count} selected {count === 1 ? 'beneficiary' : 'beneficiaries'}. This is the standing amount; it changes future months only, never a month already generated.
                    </DialogContentText>
                    <TextField
                        autoFocus margin="dense" type="number" label="Amount (ETB)" fullWidth
                        value={amount} onChange={e => setAmount(e.target.value)}
                        helperText="Leave empty to clear"
                    />
                    <TextField
                        margin="dense" label="Reason (optional)" fullWidth
                        value={note} onChange={e => setNote(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={close} disabled={busy}>Cancel</Button>
                    <Button onClick={apply} variant="contained" disabled={busy}>Set</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Checkboxes are only worth showing to finance, since the only bulk action here
// is theirs.
export const RecordBulkActions = () => {
    const { permissions } = usePermissions();
    const isFinance = !!permissions
        && (permissions.includes('finance') || permissions.includes('financeAdmin'));
    if (!isFinance) return null;
    return <SetAllowanceButton />;
};

export default RecordBulkActions;
